import React, { useState, useEffect } from 'react';
import { Row, Button, Col } from 'reactstrap';
import { Icon } from 'src/components/Component';
import Head from 'src/layout/head/Head';
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";

import HasFeature from "src/utils/has-feature";
import HasRole from "src/utils/has-role";

import AmexLogo from './images/amex.png';
import WebhooksLogo from './images/webhooks.png'
import APILogo from './images/api.png'
import OpenBankingLogo from './images/open-banking.png'
import XeroLogo from './images/xero.png';
import ZapierLogo from './images/zapier.png';
import SlackLogo from './images/slack.png';
import QuickbooksLogo from './images/quickbooks.svg';
import BrowserWindows from './images/browser-windows.png';
import WixLogo from './images/wix.png';
import ApplePayLogo from './images/apple-pay.png';
import GooglePayLogo from './images/google-pay.png';
import { API_ROUTES } from "src/config/api";
import { Auth } from "aws-amplify";
import Axios from "axios";

import { PROVIDER_CONFIGURATION } from 'src/config/providers';
import IntegrationCard from './components/integration-card';

import { ScaleIcon } from '@heroicons/react/24/outline';

const Integrations = () => {
  const { organisation: { features, is_admin, organisation }, account: { account } } = useSelector((state) => state);

  const [applePayEnabled, setApplePayEnabled] = useState(false);
  const [googlePayEnabled, setGooglePayEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  const toggleApplePay = () => {
    sendEnablementRequest('apple_pay', !applePayEnabled);
    setApplePayEnabled(!applePayEnabled);
  }

  const toggleGooglePay = () => {
    sendEnablementRequest('google_pay', !googlePayEnabled);
    setGooglePayEnabled(!googlePayEnabled);
  }

  const getPaymentSettings = async () => {
    const user = await Auth.currentAuthenticatedUser();

    const response = await Axios(
      API_ROUTES.payment['organisation:payment:settings:get'](organisation),
      {
        method: 'get',
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
      }
    );

    const settings = response.data.data;

    if (settings.length > 0) {
      setApplePayEnabled(settings[0].apple_pay_enabled);
      setGooglePayEnabled(settings[0].google_pay_enabled);
    }

    setLoading(false);
  }

  const sendEnablementRequest = async (field, value) => {
    const user = await Auth.currentAuthenticatedUser();

    await Axios(
      API_ROUTES.payment['organisation:payment:settings'](organisation),
      {
        method: 'post',
        headers: { Authorization: `Bearer ${user.signInUserSession.idToken.jwtToken}` },
        data: {
          item: `${field}_enabled`,
          value,
        }
      }
    );
  }

  useEffect(() => {
    getPaymentSettings();
  }, []);

  return (
    <React.Fragment>

      <Head title="Integrations" />


      {is_admin && (
        <div className="clearfix border border-red-200 border-stroke pl-0 py-2 bg-red-50 text-purple-900 pl-2 relative rounded-md mb-4">
          <div className="text-sm px-2 font-bold bg-red-500 text-white border-l absolute h-100 top-0 right-0 pt-[20px]">
            <strong>God Mode</strong>
          </div>

          <Link to='/organisation/integrations/payment-weights'>
            <button className="btn bg-white hover:bg-blue-300 text-red-700 border border-red-700 float-left">
              <ScaleIcon className="h-5 w-5 mr-2" />
              Manage Provider Weightings
            </button>
          </Link>
        </div>
      )}

      <Row className='mb-3 ml-1 mr-1'>
        <div className='w-full clearfix'>
          <h2 className="nk-block-title page-title text-primary">Payment Provider <span className="fw-normal">Integrations</span></h2>
        </div>
      </Row>

      <Row>
        <Col className="ml-1 mr-1">
          <Row>

            <IntegrationCard
              logo={ApplePayLogo}
              name="Apple Pay"
              description="Accept Apple Pay payments through your Felloh account."
              buttons={() => (
                <React.Fragment>
                  <Row>
                    <Col sm="12" lg="12">
                      {loading === false && (
                        <Button className={`btn-block w-100 text-center ${applePayEnabled !== true ? 'bg-red-600 border-red-600 hover:bg-red-600' : 'bg-green-600 border-green-600 hover:bg-green-600'}`} onClick={toggleApplePay}>
                          {applePayEnabled === true ? 'Enabled' : 'Disabled'}
                        </Button>
                      )}
                      {loading === true && (
                        <Button className='btn-block w-100 text-center bg-slate-800 border-gray-800'>
                          <Icon name="spinner" spin={true} /> Fetching Status...
                        </Button>
                      )}
                    </Col>
                  </Row>
                </React.Fragment>
              )}
            />

            <IntegrationCard
              logo={GooglePayLogo}
              name="Google Pay"
              description="Accept Google Pay payments through your Felloh account."
              buttons={() => (
                <React.Fragment>
                  <Row>
                    <Col sm="12" lg="12">
                      {loading === false && (
                      <Button className={`btn-block w-100 text-center ${googlePayEnabled !== true ? 'bg-red-600 border-red-600 hover:bg-red-600' : 'bg-green-600 border-green-600 hover:bg-green-600'}`} onClick={toggleGooglePay}>
                        {googlePayEnabled === true ? 'Enabled' : 'Disabled'}
                      </Button>
                      )}

                      {loading === true && (
                        <Button className='btn-block w-100 text-center bg-slate-800 border-gray-800'>
                          <Icon name="spinner" spin={true} /> Fetching Status...
                        </Button>
                      )}
                    </Col>
                  </Row>
                </React.Fragment>
              )}
            />

            {Object.keys(PROVIDER_CONFIGURATION).map((providerId, index) => {
              const provider = PROVIDER_CONFIGURATION[providerId];

              if (provider.is_beta === true) {
                return <React.Fragment key={index} />
              }

              return (
                <IntegrationCard
                  is_beta={provider.is_beta}
                  is_felloh={is_admin}
                  key={index}
                  logo={provider.logo}
                  name={provider.name}
                  description={provider.description}
                  buttons={() => (
                    <React.Fragment>
                      {(provider.admin_only === false || is_admin === true) && (
                        <React.Fragment>
                          <Link to={provider.manage_path}>
                            <Button className='btn-block w-100 text-center bg-violet-800 border-violet-800'>
                              <Icon name="setting" />Configure
                            </Button>
                          </Link>
                        </React.Fragment>
                      )}
                      {provider.admin_only === true && provider.is_beta === true && is_admin === false && (
                        <Button className='btn-green btn-block w-100 text-center' disabled>
                          Coming Soon
                        </Button>
                      )}
                      {provider.admin_only === true && (provider.is_beta === false || !provider.is_beta) && is_admin === false && (
                        <Button className='disabled:bg-red-800 disabled:border-red-800 btn-block w-100 text-center' disabled>
                          You do not have access to this feature
                        </Button>
                      )}
                    </React.Fragment>
                  )}
                />
              );
            })}

          </Row>

        </Col>
      </Row >

      <Row className="mb-3 ml-1 mr-1 mt-5">
        <h2 className="nk-block-title page-title text-primary">Developer Lead & Technical <span className="fw-normal">Integrations</span></h2>
      </Row>

      <Row>
        <Col className="ml-1 mr-1">
          <Row>


            <IntegrationCard
              logo={APILogo}
              name="Felloh API"
              description="Programatically access any of the Felloh API's to build custom integrations."
              buttons={() => (
                <React.Fragment>
                  {HasRole(account, ['developers:manage:keys']) && HasFeature(features, ['developers']) && (
                    <Row>
                      <Col sm="12" lg="6">
                        <Link to='/developers'>
                          <Button className='btn-block w-100 text-center bg-violet-800 border-violet-800'>
                            <Icon name="setting" />Configure
                          </Button>
                        </Link>
                      </Col>
                      <Col sm="12" lg="6">
                        <a href='https://developers.felloh.com' target='__blank'>
                          <Button className='btn-secondary btn-block w-100 text-center'>
                            <Icon name="setting" />Documentation
                          </Button>
                        </a>
                      </Col>
                    </Row>
                  )}
                  {(!HasFeature(features, ['developers']) || !HasRole(account, ['developers:manage:keys'])) && (
                    <Button className='disabled:bg-red-800 disabled:border-red-800 btn-block w-100 text-center' disabled>
                      You do not have access to this feature
                    </Button>
                  )}
                </React.Fragment>
              )}
            />

            <IntegrationCard
              logo={BrowserWindows}
              name="Felloh Payment Portal"
              description="Host a payment portal on your domain, allowing customers to pay themselves."
              buttons={() => (
                <React.Fragment>
                  <Row>
                    <Col sm="12" lg="6">
                      <Link to='/organisation/portal'>
                        <Button className='btn-block w-100 text-center bg-violet-800 border-violet-800'>
                          <Icon name="setting" />Configure
                        </Button>
                      </Link>
                    </Col>
                    <Col sm="12" lg="6">
                      <a href='https://developers.felloh.com/guides/setup-payment-portal' target='__blank'>
                        <Button className='btn-secondary btn-block w-100 text-center'>
                          <Icon name="setting" />Documentation
                        </Button>
                      </a>
                    </Col>
                  </Row>
                </React.Fragment>
              )}
            />

            <IntegrationCard
              logo={WebhooksLogo}
              name="Webhooks"
              description="Receive webhooks when changes happen within the felloh ecosystem."
              buttons={() => (
                <React.Fragment>
                  {HasRole(account, ['developers:manage:webhooks']) && HasFeature(features, ['developers']) && (
                    <Row>
                      <Col sm="12" lg="6">
                        <Link to='/developers'>
                          <Button className='btn-block w-100 text-center bg-violet-800 border-violet-800'>
                            <Icon name="setting" />Configure
                          </Button>
                        </Link>
                      </Col>
                      <Col sm="12" lg="6">
                        <a href='https://developers.felloh.com/webhooks' target='__blank'>
                          <Button className='btn-secondary btn-block w-100 text-center'>
                            <Icon name="setting" />Documentation
                          </Button>
                        </a>
                      </Col>
                    </Row>
                  )}
                  {(!HasFeature(features, ['developers']) || !HasRole(account, ['developers:manage:webhooks'])) && (
                    <Button className='disabled:bg-red-800 disabled:border-red-800 btn-block w-100 text-center' disabled>
                      You do not have access to this feature
                    </Button>
                  )}
                </React.Fragment>
              )}
            />

          </Row>
        </Col>
      </Row >

      <Row className="mb-3 ml-1 mr-1 mt-5">
        <h2 className="nk-block-title page-title text-primary">Other <span className="fw-normal">Integrations</span></h2>
      </Row>

      <Row>
        <Col className="ml-1 mr-1">
          <Row>

            <IntegrationCard
              logo={AmexLogo}
              name="AMEX Reconciliation Data"
              description="Reconcile Transactions and Batches from AMEX to Felloh."
              buttons={() => (
                <React.Fragment>
                  <Row>
                    <Col sm="12" lg="12">
                      <Link to='/organisation/integrations/amex'>
                        <Button className='btn-block w-100 text-center bg-violet-800 border-violet-800'>
                          <Icon name="setting" />Configure
                        </Button>
                      </Link>
                    </Col>
                  </Row>
                </React.Fragment>
              )}
            />

            <IntegrationCard
              logo={OpenBankingLogo}
              name="Open Banking Feed"
              description="Link your bank account, for easy end to end reconcilliation of payments."
              buttons={() => (
                <React.Fragment>
                  {HasFeature(features, ['aisp']) && HasRole(account, ['open-banking:manage']) && (
                    <Link to='/organisation/bank'>
                      <Button className='btn-block w-100 text-center bg-violet-800 border-violet-800'>
                        <Icon name="setting" />Configure
                      </Button>
                    </Link>
                  )}
                  {(!HasFeature(features, ['aisp']) || !HasRole(account, ['open-banking:manage'])) && (
                    <Button className='disabled:bg-red-800 disabled:border-red-800 btn-block w-100 text-center' disabled>
                      You do not have access to this feature
                    </Button>
                  )}
                </React.Fragment>
              )}
            />

            <IntegrationCard
              is_beta={true}
              logo={QuickbooksLogo}
              name="Quickbooks"
              description="Reconcile disbursements to Quickbooks from your Felloh account."
              buttons={() => (
                <Button className='btn-green btn-block w-100 text-center' disabled>
                  Coming Soon
                </Button>
              )}
            />

            <IntegrationCard
              is_beta={true}
              logo={SlackLogo}
              name="Slack"
              description="Receive payments & disbursement notifications to slack."
              buttons={() => (
                <Button className='btn-green btn-block w-100 text-center' disabled>
                  Coming Soon
                </Button>
              )}
            />

            <IntegrationCard
              logo={WixLogo}
              name="WIX"
              description="Integrate felloh payments as a custom payment provider in WIX"
              buttons={() => (
                <a href='https://developers.felloh.com/guides/accepting-payments-in-wix' target='__blank'>
                  <Button className='bg-violet-800 border-violet-800 btn-block w-100 text-center'>
                    <Icon name="setting" />Open Integraion Guide
                  </Button>
                </a>
              )}
            />


            <IntegrationCard
              is_beta={true}
              logo={XeroLogo}
              name="Xero"
              description="Reconcile disbursements to Xero from your Felloh account."
              buttons={() => (
                <Button className='btn-green btn-block w-100 text-center' disabled>
                  Coming Soon
                </Button>
              )}
            />

            <IntegrationCard
              logo={ZapierLogo}
              name="Zapier"
              description="Integrate payments with a wide range of technology providers."
              buttons={() => (
                <a href='https://zapier.com/developer/public-invite/173050/74b422d9ddbedff2e736a5c1a5f336ac/' target='__blank'>
                  <Button className='bg-violet-800 border-violet-800 btn-block w-100 text-center'>
                    <Icon name="setting" />Add to Zapier
                  </Button>
                </a>
              )}
            />


          </Row>

        </Col>
      </Row >

    </React.Fragment >
  );
};

export default Integrations;
